import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo } from 'react';
import { Input } from 'ui/styled';
import { PhoneOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import { MembersList } from 'pages/NIC/components/MembersList';
import { selectAgentSkills, selectAgentStatus, selectIsSessionActive, selectPrefilledPhoneNumber, useNicStore } from 'stores/nic/stores/nicStore';
import { selectCalls, useCallStore } from 'stores/nic/stores/callStore';
import { Tabs } from 'antd';
import useDebouncedFunction from 'utils/hooks/useDebouncedFunction';
import OutboundCall from './OutboundCall';
import CallInProgress from './CallInProgress';
import DispositionView from './DispositionView';
import { useCallStates, isCallInProgress, isOutboundCall } from '../hooks/useCallStates';
import DirectoryComponent from '../components/Agents/DirectoryComponent';
import IncomingView from './IncomingCall';
import AdditionalDirectory from '../components/TransferFlow/AdditionalDirectory';
import { useSkillActivityCallCount } from '../hooks/useSkillActivityCallCount';
import { StyledCallsCounter } from '../styled';
export var ActiveSession = function () {
    var _a, _b;
    var _c = useState(''), searchValue = _c[0], setSearchValue = _c[1];
    var _d = useState(''), debouncedValue = _d[0], setDebouncedValue = _d[1];
    var _e = useState(null), skill = _e[0], setSkill = _e[1];
    var _f = useState('members'), activeTab = _f[0], setActiveTab = _f[1];
    var calls = useCallStore(selectCalls);
    var showAdditionalDirectory = useCallStore(function (state) { return state.showAdditionalDirectory; });
    var lastCallRequireDisposition = useCallStore(function (state) { return state.lastCallRequireDisposition; });
    var currentCall = (_a = calls.find(function (c) { return c.call !== null; })) === null || _a === void 0 ? void 0 : _a.call;
    var isSessionActive = useNicStore(selectIsSessionActive);
    var agentSkills = useNicStore(selectAgentSkills);
    var agentStatus = useNicStore(selectAgentStatus);
    var prefilledPhoneNumber = useNicStore(selectPrefilledPhoneNumber);
    var setPrefilledPhoneNumber = useNicStore(function (state) { return state.setPrefilledPhoneNumber; });
    var _g = useCallStates(currentCall), hasActiveCall = _g.hasActiveCall, showMembersList = _g.showMembersList, isIncomingCall = _g.isIncomingCall;
    var activeCallsCount = useSkillActivityCallCount(isSessionActive, agentSkills);
    var memberListCallIndex = useMemo(function () {
        var idx = calls.findIndex(function (c) { return !c.call; });
        return idx !== -1 ? idx : calls.length;
    }, [calls]);
    var debouncedSearch = useDebouncedFunction(setDebouncedValue, 500);
    useEffect(function () {
        debouncedSearch(searchValue);
    }, [searchValue]);
    var isWrapUp = ((_b = agentStatus === null || agentStatus === void 0 ? void 0 : agentStatus.currentState) === null || _b === void 0 ? void 0 : _b.reason) === 'Wrap Up';
    var showDisposition = isWrapUp && lastCallRequireDisposition;
    useEffect(function () {
        if (!isSessionActive) {
            setActiveTab('members');
        }
    }, [isSessionActive]);
    useEffect(function () {
        if (prefilledPhoneNumber) {
            setSearchValue(prefilledPhoneNumber);
            setActiveTab('members');
            setPrefilledPhoneNumber(null);
        }
    }, [prefilledPhoneNumber, setPrefilledPhoneNumber]);
    var tabItems = [
        {
            key: 'members',
            label: (_jsxs("span", { children: [_jsx(PhoneOutlined, { style: { marginRight: 8 } }), "Members"] })),
            children: (_jsx("div", { children: showMembersList && (_jsx(MembersList, { agentSkills: agentSkills.filter(function (item) { return item.isOutbound; }), skill: skill, setSkill: setSkill, dialNumber: debouncedValue, disabled: activeTab === 'agents', callIndex: memberListCallIndex })) }))
        },
        {
            key: 'agents',
            disabled: !isSessionActive,
            label: (_jsxs("span", { children: [_jsx(TeamOutlined, { style: { marginRight: 8 } }), "Agents"] })),
            children: showMembersList && (_jsx(DirectoryComponent, { searchText: debouncedValue }))
        }
    ];
    return (_jsxs("div", { style: { display: 'flex', flexDirection: 'column' }, children: [calls.map(function (item, index) {
                var _a;
                var status = (_a = item.call) === null || _a === void 0 ? void 0 : _a.status;
                if (isCallInProgress(status)) {
                    return _jsx(CallInProgress, { callIndex: index }, "nic-call-".concat(index + 1));
                }
                if (isOutboundCall(status)) {
                    return _jsx(OutboundCall, { callIndex: index }, "nic-call-".concat(index + 1));
                }
                return null;
            }), isIncomingCall && _jsx(IncomingView, {}), showDisposition && _jsx(DispositionView, { skill: skill, currentCall: currentCall }), showAdditionalDirectory && _jsx(AdditionalDirectory, {}), !hasActiveCall && !showDisposition && (_jsxs(_Fragment, { children: [_jsx("div", { style: { padding: 16 }, children: _jsx(Input, { prefix: _jsx(SearchOutlined, {}), value: searchValue, onChange: function (e) {
                                var value = e.target.value;
                                var formattedPhone = activeTab === 'members'
                                    ? value.replace(/\D/g, '')
                                    : value;
                                setSearchValue(formattedPhone);
                            }, placeholder: activeTab === 'members'
                                ? 'Type in a phone number'
                                : 'Search agents by name or team', disabled: !isSessionActive, allowClear: true }) }), _jsx(Tabs, { items: tabItems, onChange: function (key) { return setActiveTab(key); }, activeKey: activeTab, size: "small", style: { padding: '0 16px' } }), _jsxs(StyledCallsCounter, { children: ["Contacts waiting: ", activeCallsCount] })] }))] }));
};
